import { useState, type ReactNode } from 'react';
import { Button } from '../../merchant/ui/primitives.js';
import {
  contentLanguage,
  instagramReferenceUrl,
  researchUi,
  type ResearchReference,
} from './researchPresentation.js';

/** Local presentation state only; iframe load is never recorded as observed playback. */
export function ReferenceDetails({
  reference,
  language,
}: {
  readonly reference: ResearchReference;
  readonly language: 'en' | 'ar';
}): ReactNode {
  const copy = researchUi[language];
  const [open, setOpen] = useState(false);
  const original = instagramReferenceUrl(reference);
  const canPreview =
    original !== null &&
    reference.mediaKind === 'VIDEO' &&
    reference.playback?.method === 'OFFICIAL_EMBED' &&
    reference.playback.resolution === 'RESOLVED' &&
    reference.playback.playback !== 'OBSERVED_FAILED';
  return (
    <details
      className="mj-ws-details mj-ws-reference-details"
      onToggle={(event) => {
        if (!event.currentTarget.open) setOpen(false);
      }}
    >
      <summary>{copy.details}</summary>
      <dl className="mj-ws-rdl">
        <dt>{copy.source}</dt>
        <dd>{contentLanguage(reference.sourceLanguage, language)}</dd>
        <dt>{copy.caption}</dt>
        <dd dir="auto" className="mj-ws-reference-caption">
          {reference.caption ?? copy.unavailable}
        </dd>
      </dl>
      {canPreview ? (
        <>
          <p className="mj-ws-muted mj-ws-xs">{copy.previewNote}</p>
          <Button
            small
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? copy.close : copy.preview}
          </Button>
          {open ? (
            <iframe
              className="mj-ws-reference-player"
              src={`${original}embed/`}
              title={copy.preview}
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
              allow="fullscreen; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : null}
        </>
      ) : null}
      {original !== null ? (
        <p>
          <a href={original} target="_blank" rel="noopener noreferrer">
            {copy.original}
          </a>
        </p>
      ) : null}
    </details>
  );
}
